/**
 * Utility functions to collect visitor information for analytics
 *
 * GDPR Compliance: Geolocation data collection requires user consent.
 * - setGeoConsentStatus(true/false) - Set user's consent preference
 * - hasGeoConsent() - Check if user has consented to geolocation
 * - Geolocation is only collected if consent is given
 */

import { logger } from './logger';

// Storage key for GDPR geolocation consent
const GEO_CONSENT_KEY = 'g6th_sense_geo_consent';
const GEO_CONSENT_ASKED_KEY = 'g6th_sense_geo_consent_asked';

/**
 * Check if user has consented to geolocation tracking
 * @returns {boolean} true if user has consented, false otherwise
 */
export const hasGeoConsent = () => {
  try {
    return localStorage.getItem(GEO_CONSENT_KEY) === 'true';
  } catch {
    // localStorage may be unavailable (e.g., private browsing mode)
    return false;
  }
};

/**
 * Check if user has been asked about geolocation consent
 * @returns {boolean} true if user has been asked
 */
export const hasBeenAskedGeoConsent = () => {
  try {
    return localStorage.getItem(GEO_CONSENT_ASKED_KEY) === 'true';
  } catch {
    // localStorage may be unavailable (e.g., private browsing mode)
    return false;
  }
};

/**
 * Set user's geolocation consent status
 * @param {boolean} consent - Whether user consents to geolocation tracking
 */
export const setGeoConsentStatus = (consent) => {
  try {
    localStorage.setItem(GEO_CONSENT_KEY, consent ? 'true' : 'false');
    localStorage.setItem(GEO_CONSENT_ASKED_KEY, 'true');
    logger.log('[visitorInfo] Geolocation consent set to:', consent);
  } catch (e) {
    logger.error('[visitorInfo] Failed to save geo consent:', e);
  }
};

/**
 * Clear geolocation consent (for testing or user request)
 */
export const clearGeoConsent = () => {
  try {
    localStorage.removeItem(GEO_CONSENT_KEY);
    localStorage.removeItem(GEO_CONSENT_ASKED_KEY);
    sessionStorage.removeItem('g6th_sense_geo_data');
  } catch (e) {
    logger.error('[visitorInfo] Failed to clear geo consent:', e);
  }
};

// Helper: Detect browser from user agent
const detectBrowser = (ua) => {
  if (ua.includes('Firefox/')) {
    return {
      browser: 'Firefox',
      browser_version: ua.split('Firefox/')[1]?.split(' ')[0] || ''
    };
  }
  
  if (ua.includes('Edg/')) {
    return {
      browser: 'Edge',
      browser_version: ua.split('Edg/')[1]?.split(' ')[0] || ''
    };
  }
  
  if (ua.includes('Chrome/')) {
    return {
      browser: 'Chrome',
      browser_version: ua.split('Chrome/')[1]?.split(' ')[0] || ''
    };
  }
  
  if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    return {
      browser: 'Safari',
      browser_version: ua.split('Version/')[1]?.split(' ')[0] || ''
    };
  }
  
  if (ua.includes('MSIE') || ua.includes('Trident/')) {
    return {
      browser: 'Internet Explorer',
      browser_version: ''
    };
  }
  
  return {
    browser: 'Unknown',
    browser_version: ''
  };
};

// Helper: Detect Windows OS version
const detectWindowsVersion = (ua) => {
  if (ua.includes('Windows NT 10.0')) return '10';
  if (ua.includes('Windows NT 6.3')) return '8.1';
  if (ua.includes('Windows NT 6.2')) return '8';
  if (ua.includes('Windows NT 6.1')) return '7';
  return '';
};

// Helper: Detect operating system
const detectOS = (ua) => {
  if (ua.includes('Win')) {
    return {
      os: 'Windows',
      os_version: detectWindowsVersion(ua)
    };
  }
  
  if (ua.includes('Mac')) {
    const match = ua.match(/Mac OS X (\d+[._]\d+[._]\d+)/);
    return {
      os: 'macOS',
      os_version: match ? match[1].replaceAll('_', '.') : ''
    };
  }
  
  if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    return {
      os: 'Android',
      os_version: match ? match[1] : ''
    };
  }
  
  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+_\d+(_\d+)?)/);
    return {
      os: 'iOS',
      os_version: match ? match[1].replaceAll('_', '.') : ''
    };
  }
  
  if (ua.includes('Linux')) {
    return {
      os: 'Linux',
      os_version: ''
    };
  }
  
  return {
    os: 'Unknown',
    os_version: ''
  };
};

// Helper: Detect device type
const detectDeviceType = (ua) => {
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
    return 'mobile';
  }
  
  if (ua.includes('Tablet') || ua.includes('iPad')) {
    return 'tablet';
  }
  
  return 'desktop';
};

// Parse user agent to extract browser and OS information
export const parseUserAgent = () => {
  const ua = navigator.userAgent;
  
  const browserInfo = detectBrowser(ua);
  const osInfo = detectOS(ua);
  const deviceType = detectDeviceType(ua);
  
  return {
    user_agent: ua,
    ...browserInfo,
    ...osInfo,
    device_type: deviceType
  };
};

// Get session duration in seconds (per browser session - resets when tab closes)
export const getSessionDuration = () => {
  try {
    // Use sessionStorage so duration resets for each browser session
    const sessionStart = sessionStorage.getItem('g6th_sense_session_start');
    if (!sessionStart) {
      const now = Date.now();
      sessionStorage.setItem('g6th_sense_session_start', now.toString());
      return 0;
    }
    const start = Number.parseInt(sessionStart, 10);
    const now = Date.now();
    return Math.floor((now - start) / 1000); // Return seconds
  } catch {
    // sessionStorage may be unavailable
    return 0;
  }
};

// Get session start time as ISO string (for current_visit_started_at)
export const getSessionStartISO = () => {
  try {
    let sessionStart = sessionStorage.getItem('g6th_sense_session_start');
    if (!sessionStart) {
      const now = Date.now();
      sessionStorage.setItem('g6th_sense_session_start', now.toString());
      sessionStart = now.toString();
    }
    return new Date(Number.parseInt(sessionStart, 10)).toISOString();
  } catch {
    return new Date().toISOString();
  }
};

// Module-level storage for the host page title received via postMessage (SPA navigation updates)
let _hostPageTitle = null;

// Called by ChatWidget when a g6th-sense-page-change postMessage arrives from the parent page
export const setHostPageTitle = (title) => {
  _hostPageTitle = title || null;
};

// Get page title from document or derive from URL
const getPageTitle = (pageUrl) => {
  try {
    // 1. Prefer title updated via postMessage from parent page (SPA navigation after iframe load)
    if (_hostPageTitle && _hostPageTitle.trim()) {
      return _hostPageTitle.trim();
    }

    // 2. For iframe-embedded widget: use pageTitle URL param passed by widget-embed.js on load
    // Note: URLSearchParams.get() already decodes percent-encoded values — no need for decodeURIComponent
    const urlParams = new URLSearchParams(globalThis.location.search);
    const hostPageTitle = urlParams.get('pageTitle');
    if (hostPageTitle && hostPageTitle.trim()) {
      return hostPageTitle.trim();
    }

    // 3. Fallback: read document.title (only correct when widget is NOT inside an iframe)
    if (document.title && document.title.trim()) {
      return document.title.trim();
    }

    // 4. Derive a readable name from the URL path
    const url = new URL(pageUrl, globalThis.location.origin);
    const pathSegments = url.pathname.split('/').filter(Boolean);

    if (pathSegments.length > 0) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      return lastSegment
        .replace(/\.[^.]+$/, '') // Remove file extension
        .replace(/[-_]/g, ' ')   // Replace hyphens/underscores with spaces
        .replace(/\b\w/g, c => c.toUpperCase()); // Capitalize first letter of each word
    }

    return 'Home';
  } catch {
    return pageUrl;
  }
};

// Track visited pages with timestamps and time spent
// Uses sessionStorage so pages are cleared when browser tab closes (per-session tracking)
export const trackPageVisit = (pageUrl, pageTitle = null) => {
  try {
    // Don't track admin pages
    const pageLower = pageUrl.toLowerCase();
    if (pageLower.includes('/admin') || pageLower.includes('/login') || pageLower.includes('/agent')) {
      return;
    }

    const now = new Date().toISOString();
    const visited = JSON.parse(sessionStorage.getItem('g6th_sense_visited_pages') || '[]');

    // Get the page title - use provided title, or try to get from document, or fallback to URL path
    const title = pageTitle || getPageTitle(pageUrl);

    // Check if the last page is the same URL - if so, don't add duplicate
    if (visited.length > 0) {
      const lastPage = visited[visited.length - 1];
      if (lastPage.page === pageUrl) {
        // Same page, don't add duplicate - just keep tracking time on current page
        return;
      }
      // Different page - close out the previous page
      if (!lastPage.exited_at) {
        lastPage.exited_at = now;
        const enteredTime = new Date(lastPage.entered_at).getTime();
        const exitedTime = new Date(now).getTime();
        lastPage.time_spent = Math.floor((exitedTime - enteredTime) / 1000); // seconds
      }
    }

    // Add new page visit
    visited.push({
      page: pageUrl,
      title: title,
      entered_at: now,
      exited_at: null,
      time_spent: 0
    });

    sessionStorage.setItem('g6th_sense_visited_pages', JSON.stringify(visited));
  } catch (e) {
    logger.error('Error tracking page visit:', e);
  }
};

// Get visited pages with time tracking
// Uses sessionStorage so only current session pages are returned
export const getVisitedPages = () => {
  try {
    const visited = JSON.parse(sessionStorage.getItem('g6th_sense_visited_pages') || '[]');

    // Update current page's time spent
    if (visited.length > 0) {
      const lastPage = visited[visited.length - 1];
      if (!lastPage.exited_at) {
        const enteredTime = new Date(lastPage.entered_at).getTime();
        const now = Date.now();
        lastPage.time_spent = Math.floor((now - enteredTime) / 1000); // seconds
      }
    }

    return visited;
  } catch {
    // sessionStorage may be unavailable
    return [];
  }
};

// Get current tenant ID from visitorTokenManager (imported dynamically to avoid circular deps)
const getCurrentTenantId = () => {
  try {
    // Try to get tenant ID from the stored widget_tenant_id
    return localStorage.getItem('widget_tenant_id') || null;
  } catch {
    // localStorage may be unavailable
    return null;
  }
};

// Get tenant-specific storage key
const getTenantKey = (baseKey) => {
  const tenantId = getCurrentTenantId();
  if (!tenantId) {
    return baseKey; // Fallback to global key if no tenant context
  }
  return `${tenantId}_${baseKey}`;
};

// Track visit count (with session guard to prevent double-increment)
// Now tenant-specific: each tenant tracks visits independently
export const incrementVisitCount = () => {
  try {
    const tenantKey = getTenantKey('g6th_sense_visit_count');
    const sessionGuardKey = getTenantKey('g6th_visit_incremented');

    // Check if we already incremented in this session (prevents double-count from React Strict Mode or re-renders)
    const alreadyIncremented = sessionStorage.getItem(sessionGuardKey);
    if (alreadyIncremented) {
      // Already incremented this session, just return current count
      return Number.parseInt(localStorage.getItem(tenantKey) || '1', 10);
    }

    const count = Number.parseInt(localStorage.getItem(tenantKey) || '0', 10);
    const newCount = count + 1;
    localStorage.setItem(tenantKey, newCount.toString());

    // Mark that we've incremented in this session
    sessionStorage.setItem(sessionGuardKey, 'true');

    return newCount;
  } catch {
    // Storage may be unavailable
    return 1;
  }
};

// Get visit count (tenant-specific)
export const getVisitCount = () => {
  try {
    const tenantKey = getTenantKey('g6th_sense_visit_count');
    return Number.parseInt(localStorage.getItem(tenantKey) || '1', 10);
  } catch {
    // localStorage may be unavailable
    return 1;
  }
};

// Check if first visit (tenant-specific)
export const isFirstVisit = () => {
  try {
    const tenantKey = getTenantKey('g6th_sense_visit_count');
    return !localStorage.getItem(tenantKey);
  } catch {
    // localStorage may be unavailable
    return true;
  }
};

// Get visitor IP and geolocation data (with caching to avoid repeated API calls)
// GDPR: Only fetches geolocation if user has consented
export const getVisitorIP = async () => {
  try {
    // GDPR: Check for geolocation consent before collecting location data
    const consentGiven = hasGeoConsent();

    // Check if we already have cached geo data in sessionStorage
    const cachedGeo = sessionStorage.getItem('g6th_sense_geo_data');
    if (cachedGeo) {
      const geoData = JSON.parse(cachedGeo);
      if (geoData.ip) {
        return geoData.ip; // Return cached IP, skip external API call
      }
    }

    // If no consent, don't fetch geolocation data
    if (!consentGiven) {
      logger.log('[visitorInfo] Geolocation skipped - no consent');
      return null;
    }

    // User consented - fetch from API
    const response = await fetch('https://ipwho.is/');
    const data = await response.json();

    if (data.success && data.ip) {
      // Store geolocation data for later use
      const geoData = {
        ip: data.ip,
        country: data.country,
        countryCode: data.country_code,
        region: data.region,
        city: data.city,
        zip: data.postal,
        lat: data.latitude,
        lon: data.longitude,
        timezone: data.timezone?.id,
        isp: data.connection?.isp
      };

      // Cache the geolocation data in sessionStorage
      sessionStorage.setItem('g6th_sense_geo_data', JSON.stringify(geoData));

      return data.ip; // Return IP address
    }
    return null;
  } catch {
    // Fallback to ipify if ipwho.is fails (IP only, no geo data)
    // Still requires consent since IP reveals location
    if (!hasGeoConsent()) {
      return null;
    }
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      // Both IP services failed, return null gracefully
      return null;
    }
  }
};

// Get cached geolocation data
export const getGeoLocation = () => {
  try {
    const cached = sessionStorage.getItem('g6th_sense_geo_data');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // sessionStorage may be unavailable or data corrupted
    logger.log('Could not get geo data');
  }
  return null;
};

// Check if current page is an admin page (should not be tracked)
export const isAdminPage = () => {
  const path = globalThis.location.pathname.toLowerCase();
  return path.includes('/admin') || path.includes('/login') || path.includes('/agent');
};

// Get the host site URL (for embedded widgets in iframe)
// This returns the actual website URL where the widget is embedded
export const getHostSiteUrl = () => {
  // First, check if hostUrl was passed via URL parameter (from widget-embed.js)
  try {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const hostUrl = urlParams.get('hostUrl');
    if (hostUrl) {
      return decodeURIComponent(hostUrl);
    }
  } catch {
    // URL parsing failed, continue with other checks
  }

  // Check if we're in an iframe
  try {
    if (globalThis.self !== globalThis.top) {
      // In iframe mode - try to get parent URL from referrer
      if (document.referrer) {
        return document.referrer;
      }
      // If we can't get parent URL, return iframe marker
      return `[iframe] ${globalThis.location.href}`;
    }
  } catch {
    // Cross-origin iframe access denied - we're in an iframe
    if (document.referrer) {
      return document.referrer;
    }
    return `[iframe] ${globalThis.location.href}`;
  }

  // Default to current URL (not in iframe)
  return globalThis.location.href;
};

// Get current page URL (considering embedded context)
export const getCurrentPageUrl = () => {
  return getHostSiteUrl();
};

// Get visitor information (full version - only use for session initialization)
export const getVisitorInfo = async () => {
  const uaInfo = parseUserAgent();

  // Try to get IP address and geolocation (will be null if fails)
  let ipAddress = null;
  let geoData = null;
  try {
    ipAddress = await getVisitorIP();
    geoData = getGeoLocation(); // Get cached geo data after IP fetch
  } catch {
    // IP fetch failed, continue without it
    logger.log('Could not fetch IP address');
  }

  return {
    ...uaInfo,
    ip_address: ipAddress,
    // Geolocation data
    country: geoData?.country || null,
    country_code: geoData?.countryCode || null,
    region: geoData?.region || null,
    city: geoData?.city || null,
    zip: geoData?.zip || null,
    latitude: geoData?.lat || null,
    longitude: geoData?.lon || null,
    isp: geoData?.isp || null,
    // Other info
    screen_resolution: `${globalThis.screen.width}x${globalThis.screen.height}`,
    timezone: geoData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language || navigator.userLanguage,
    referrer: document.referrer || 'Direct',
    current_page: getCurrentPageUrl(),
    current_page_title: getPageTitle(getCurrentPageUrl()),
    host_site: getHostSiteUrl(),
    visited_pages: getVisitedPages(),
    visit_count: getVisitCount(),
    first_visit: isFirstVisit(),
    session_duration: getSessionDuration(),
    current_visit_started_at: getSessionStartISO()
  };
};

// Get minimal visitor info for chat messages (lightweight - no external API calls)
// Only includes data that might change during a session
export const getMinimalVisitorInfo = () => {
  return {
    current_page: getCurrentPageUrl(),
    session_duration: getSessionDuration()
  };
};

// Initialize visitor tracking
export const initVisitorTracking = () => {
  // Don't track admin pages
  if (isAdminPage()) {
    return;
  }

  // Track current page immediately - use host site URL for embedded widgets
  const pageUrl = getCurrentPageUrl();
  trackPageVisit(pageUrl);

  // Increment visit count
  incrementVisitCount();

  // Track page changes (for SPAs)
  if (globalThis.history?.pushState) {
    const originalPushState = globalThis.history.pushState;
    globalThis.history.pushState = function(...args) {
      originalPushState.apply(globalThis.history, args);
      trackPageVisit(getCurrentPageUrl());
    };

    const originalReplaceState = globalThis.history.replaceState;
    globalThis.history.replaceState = function(...args) {
      originalReplaceState.apply(globalThis.history, args);
      trackPageVisit(getCurrentPageUrl());
    };
  }

  // Track back/forward navigation
  globalThis.addEventListener('popstate', () => {
    trackPageVisit(getCurrentPageUrl());
  });

  // Track page unload to update time spent
  globalThis.addEventListener('beforeunload', () => {
    const visited = JSON.parse(sessionStorage.getItem('g6th_sense_visited_pages') || '[]');
    if (visited.length > 0) {
      const lastPage = visited[visited.length - 1];
      if (!lastPage.exited_at) {
        lastPage.exited_at = new Date().toISOString();
        const enteredTime = new Date(lastPage.entered_at).getTime();
        lastPage.time_spent = Math.floor((Date.now() - enteredTime) / 1000);
        sessionStorage.setItem('g6th_sense_visited_pages', JSON.stringify(visited));
      }
    }
  });
};
